/**
 * useProfiles — Main hook for fetching and managing multiple user profiles.
 */
import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useProfilesState } from './useProfilesState';
import { useProfileQueries } from './useProfileQueries';
import { useProfileUtils } from './useProfileUtils';

export function useProfiles(userIds: string[]) {
  // Get state and state setters
  const state = useProfilesState();
  const { 
    profiles,
    loading, 
    error, 
    fetchedIds, 
    setFetchedIds, 
    refreshTrigger, 
    setRefreshTrigger
  } = state;

  // Get query functions
  const { fetchUserProfiles } = useProfileQueries(state);

  // Stabilize userIds to prevent infinite re-render loops
  const stableUserIds = useMemo(() => {
    const sorted = (userIds || []).filter(Boolean).sort();
    return sorted;
  }, [JSON.stringify((userIds || []).filter(Boolean).sort())]);

  // Force refetching of specific user IDs
  const refetchUserProfiles = useCallback(async (idsToRefetch: string[]) => {
    if (!idsToRefetch || idsToRefetch.length === 0) return;
    
    const validIdsToRefetch = idsToRefetch.filter(id => !!id);
    if (validIdsToRefetch.length === 0) return;
    
    // Clear these IDs from the fetchedIds set to force a refetch
    setFetchedIds(prev => {
      const newSet = new Set(prev);
      validIdsToRefetch.forEach(id => newSet.delete(id));
      return newSet;
    });
    
    // Now fetch these profiles again
    await fetchUserProfiles(validIdsToRefetch);
  }, [fetchUserProfiles, setFetchedIds]);

  // Get profile utils
  const { getDisplayName } = useProfileUtils(state, refetchUserProfiles);

  // Force refresh all profiles by clearing the fetchedIds cache
  const refreshProfiles = useCallback(() => {
    setFetchedIds(new Set());
    setRefreshTrigger(prev => prev + 1);
  }, [setFetchedIds, setRefreshTrigger]);

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Fetch profiles for the userIds array - only for new IDs
  useEffect(() => {
    if (stableUserIds.length === 0) return;
    
    const newUserIds = stableUserIds.filter(id => !fetchedIds.has(id));
    if (newUserIds.length === 0) return;
    if (isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    fetchUserProfiles(newUserIds).finally(() => {
      isFetchingRef.current = false;
    });
  }, [stableUserIds, fetchedIds, refreshTrigger, fetchUserProfiles]);

  return {
    profiles,
    loading,
    error,
    getDisplayName,
    refreshProfiles,
    refetchUserProfiles
  };
}

// Re-export interface for consumers
export interface Profile {
  id: string;
  display_name?: string;
  email?: string;
}
